import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router"
import { getToken } from "@clerk/vue"
import { Capacitor } from "@capacitor/core"
import { App } from "@capacitor/app"

const routes: Array<RouteRecordRaw> = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/auth/LoginView.vue")
  },
  {
    path: "/invite",
    name: "invite",
    component: () => import("@/views/auth/InviteView.vue")
  },
  {
    path: "/",
    name: "layout-columns-view",
    component: () => import("@/views/LayoutColumnsView.vue"),
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: "/",
        name: "instruction-list",
        components: {
          app: () => import("@/mobile_app/views/InstructionListView.vue")
        }
      },
      {
        path: "app/time-registration",
        name: "time-registration",
        components: {
          app: () => import("@/mobile_app/views/WorkPeriodListView.vue")
        }
      },
      {
        path: "app/templates",
        name: "templates",
        components: {
          app: () => import("@/mobile_app/views/TemplateManagementView.vue")
        }
      },
      {
        path: "app/templates/dienst/:templateId",
        name: "work-period-template-form",
        components: {
          app: () => import("@/mobile_app/views/WorkPeriodTemplateFormView.vue")
        }
      },
      {
        path: "app/templates/werkzaamheid/:templateId",
        name: "instruction-activity-template-form",
        components: {
          app: () => import("@/mobile_app/views/InstructionFeedbackActivityTemplateFormView.vue")
        }
      },
      {
        path: "app/file",
        name: "file-viewer",
        components: {
          app: () => import("@/mobile_app/views/FileViewerView.vue")
        }
      },
      {
        path: "app/:id",
        name: "instruction-details",
        components: {
          app: () => import("@/mobile_app/views/InstructionDetailsView.vue")
        }
      },
      {
        path: "app/:id/:activityId",
        name: "instruction-activity-details",
        components: {
          app: () => import("@/mobile_app/views/InstructionFeedbackActivityView.vue")
        }
      }
    ]
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: () => import("@/dashboard/layouts/DashboardLayout.vue"),
    meta: {
      dashboard: true
    },
    children: [
      {
        path: "",
        name: "dashboard-home",
        component: () => import("@/dashboard/views/DashboardView.vue"),
        meta: {
          title: "Dashboard",
          dashboard: true
        }
      },
      {
        path: "service-providers-businesses",
        name: "dashboard-service-providers-businesses",
        component: () => import("@/dashboard/views/ServiceProvidersBusinessesView.vue"),
        meta: {
          title: "Dienstverleners en Bedrijven",
          dashboard: true
        }
      },
      {
        path: "items",
        name: "dashboard-items",
        component: () => import("@/dashboard/views/ItemsView.vue"),
        meta: {
          title: "Artikelen",
          dashboard: true
        }
      },
      {
        path: "rental-intakes-dispenses",
        name: "dashboard-rental-intakes-dispenses",
        component: () => import("@/dashboard/views/RentalIntakesDispensesView.vue"),
        meta: {
          title: "Inhuur- en verhuurmutaties",
          dashboard: true
        }
      },
      {
        path: "project-overview",
        name: "dashboard-project-overview",
        component: () => import("@/dashboard/views/ProjectOverviewView.vue"),
        meta: {
          title: "Projectoverzicht",
          dashboard: true
        }
      },
      {
        path: "planning",
        name: "dashboard-planning",
        component: () => import("@/dashboard/views/PlanningGridView.vue"),
        meta: {
          title: "Planning",
          dashboard: true
        }
      },
      {
        path: "users-map",
        name: "dashboard-users-map",
        component: () => import("@/dashboard/views/UsersMapView.vue"),
        meta: {
          title: "Dienstverlenerkaart",
          dashboard: true
        }
      }
    ]
  },
  {
    path: "/error",
    name: "error",
    component: () => import("@/views/ErrorView.vue")
  },
  // Customer portal routes (public with token)
  {
    path: "/customer",
    name: "customer",
    component: () => import("@/customer/layouts/CustomerLayout.vue"),
    meta: {
      requiresAuth: false,
      customer: true
    },
    children: [
      {
        path: "order/:token",
        name: "customer-order-tracking",
        component: () => import("@/customer/views/OrderTrackingView.vue"),
        props: true,
        meta: {
          title: "Order Tracking",
          customer: true
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If we have a saved position (from browser back/forward), use it
    if (savedPosition) {
      return savedPosition
    }

    // For routes with keep-alive, don't auto-scroll - let the components handle it
    const keepAliveRoutes = ["/", "/dashboard"]
    if (keepAliveRoutes.includes(to.path) || keepAliveRoutes.includes(from.path)) {
      return false
    }

    // Default: scroll to top for other navigations
    return { top: 0 }
  }
})

// Deep linking handler: https://capacitorjs.com/docs/guides/deep-links
App.addListener("appUrlOpen", function (event) {
  const slug = event.url.startsWith("peanuts://") ? event.url.replace("peanuts://", "") : ""

  router
    .push({
      path: slug || "/"
    })
    .then(() => {
      // full reload the page on android
      if (Capacitor.getPlatform() === "android") {
        router.go(0)
      }
    })
})

router.beforeEach(async (to) => {
  if (to.meta.requiresAuth) {
    const isAuthenticated = (await getToken()) !== null
    if (!isAuthenticated) {
      return { name: "login" }
    }
  }
})

// Register Capacitor back button handler
document.addEventListener(
  "ionBackButton",
  (ev: any) => {
    ev.detail.register(10, () => {
      if (router.currentRoute.value.path !== "/") {
        router.back()
      } else {
        App.exitApp()
      }
    })
  },
  false
)

export default router
